/**
 * Shared typed classification contract for the Jarvis IGP layer-14
 * `properties-boundaries` `MgmtType` field.
 *
 * This is the authoritative visual classification source for the Prism
 * boundary map (fills, outlines, pins, clusters, legend, and search
 * grouping). It is intentionally separate from the `ManagedBy` field/helpers
 * in `./layerTypes.ts`, which remain in place only for their existing
 * out-of-scope consumers (AI Chat, Jarvis backend reporting, and the
 * property table's land-use `managementType` column) and must not be reused
 * for `MgmtType` values.
 *
 * Field codes are confirmed against
 * `packages/map/docs/map-service-defs/jarvis/igp-gis-layers/properties-boundaries.json`
 * (`MgmtType` domain `ManagementType`): `DirectOp`, `Lease`, `Contract`.
 *
 * `null`, blank, and any unexpected/unrecognized value are valid
 * data-quality states and must classify explicitly as `Unknown` — never as a
 * default valid category and never inferred from `ManagedBy`.
 */

import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import { propertyColors } from './constants';
import { getDirectOperatedPin, getLeasePin, getThirdPartyPin, getUnknownMgmtTypePin } from '../components/pins';
import { pinSymbolDefaultConfig } from './utils';
import { defaultFeatureReductionV2 } from './featureReductions';
type FeatureReductionCluster = __esri.FeatureReductionCluster;

/**
 * The exact GIS coded values for layer-14 `MgmtType`, as defined by the
 * `ManagementType` domain. These are renderer/query values, not display
 * labels.
 */
export const MGMT_TYPE_CODES = {
  DirectOp: 'DirectOp',
  Lease: 'Lease',
  Contract: 'Contract'
} as const;

/** A confirmed layer-14 `MgmtType` GIS coded value. */
export type MgmtTypeCode = (typeof MGMT_TYPE_CODES)[keyof typeof MGMT_TYPE_CODES];

/**
 * Explicit classification key used throughout the map presentation layer.
 * Includes `Unknown` as a first-class category — distinct from, and never a
 * silent fallback for, any valid `MgmtTypeCode`.
 */
export type MgmtTypeClassification = MgmtTypeCode | 'Unknown';

/** The gray "Unknown" classification key. Never used as an authorization signal. */
export const MGMT_TYPE_UNKNOWN = 'Unknown' as const;

/** The field name on the layer-14 properties-boundaries layer that carries this classification. */
export const MGMT_TYPE_FIELD = 'MgmtType' as const;

/**
 * Visual + display metadata for one `MgmtType` classification, used to keep
 * boundary fills/outlines, pins, clusters, legend entries, and search chips
 * in agreement.
 */
export interface MgmtTypeVisual {
  /** The classification key (`DirectOp` | `Lease` | `Contract` | `Unknown`). */
  code: MgmtTypeClassification;
  /** Human-readable label shown in legend, search chips, cluster labels, and tooltips. */
  label: string;
  /** Boundary fill/outline and pin color for this category. */
  color: string;
  /** Pin image factory for this category. */
  getPin: () => string;
}

/**
 * The single shared visual+display metadata map for all three valid
 * `MgmtType` categories plus the explicit gray `Unknown` state. Renderers,
 * pin layers, cluster symbols, legend, and map search must all derive their
 * colors/pins/labels from this map rather than duplicating values.
 */
export const mgmtTypeVisuals: Record<MgmtTypeClassification, MgmtTypeVisual> = {
  DirectOp: {
    code: 'DirectOp',
    label: 'Directly Operated',
    color: propertyColors.directOperations,
    getPin: getDirectOperatedPin
  },
  Lease: {
    code: 'Lease',
    label: 'Lease Property',
    color: propertyColors.lease,
    getPin: getLeasePin
  },
  Contract: {
    code: 'Contract',
    label: 'Contractor Managed',
    color: propertyColors.contractManaged,
    getPin: getThirdPartyPin
  },
  Unknown: {
    code: 'Unknown',
    // Distinct gray treatment — must never visually imply "Contractor
    // Managed", "Other", or any authorized/unauthorized status.
    label: 'Unknown',
    color: propertyColors.unknownMgmtType,
    getPin: getUnknownMgmtTypePin
  }
};

/** Ordered list of the three valid (non-Unknown) `MgmtType` classifications, for legend/search iteration. */
export const validMgmtTypeCodes: MgmtTypeCode[] = [
  MGMT_TYPE_CODES.DirectOp,
  MGMT_TYPE_CODES.Lease,
  MGMT_TYPE_CODES.Contract
];

/**
 * Classifies a raw `MgmtType` attribute value (as read off a layer-14
 * feature or synthetic map graphic) into the shared classification key.
 *
 * `null`, `undefined`, blank/whitespace-only strings, and any value that is
 * not one of the three confirmed GIS codes all classify as `Unknown`. This
 * must be the only path used to derive the "no boundary MgmtType" gray
 * state — it must never be substituted with the land-use table's
 * `managementType`/`mgmtType` value.
 *
 * @param rawMgmtType - The raw `MgmtType` value from a feature attribute or synthetic graphic, if any.
 * @returns The classification key: `DirectOp`, `Lease`, `Contract`, or `Unknown`.
 *
 * @example
 * classifyMgmtType('DirectOp') // 'DirectOp'
 * classifyMgmtType('  ') // 'Unknown'
 * classifyMgmtType(null) // 'Unknown'
 * classifyMgmtType('SomeUnexpectedValue') // 'Unknown'
 */
export function classifyMgmtType(rawMgmtType: string | null | undefined): MgmtTypeClassification {
  if (typeof rawMgmtType !== 'string') return MGMT_TYPE_UNKNOWN;
  const trimmed = rawMgmtType.trim();
  if (!trimmed) return MGMT_TYPE_UNKNOWN;
  if ((validMgmtTypeCodes as string[]).includes(trimmed)) {
    return trimmed as MgmtTypeCode;
  }
  return MGMT_TYPE_UNKNOWN;
}

/**
 * Looks up the shared visual + display metadata for a raw `MgmtType` value,
 * classifying it first via {@link classifyMgmtType}. This is the single
 * entry point renderers, pin layers, cluster symbols, legend, and map
 * search should use to resolve color/pin/label together.
 *
 * @param rawMgmtType - The raw `MgmtType` value from a feature attribute or synthetic graphic, if any.
 * @returns The `MgmtTypeVisual` metadata for the resolved classification.
 *
 * @example
 * getMgmtTypeVisual('Lease').label // 'Lease Property'
 * getMgmtTypeVisual(undefined).label // 'Unknown'
 */
export function getMgmtTypeVisual(rawMgmtType: string | null | undefined): MgmtTypeVisual {
  return mgmtTypeVisuals[classifyMgmtType(rawMgmtType)];
}

const makeMgmtTypeSymbol = (pinUrl: string) => ({
  type: 'picture-marker',
  width: `${32 * 1.2}px`,
  height: `${38 * 1.2}px`,
  url: pinUrl
} as any);

/** SimpleRenderer for each valid `MgmtType` category's pin layer, plus the explicit gray Unknown pin. */
export const mgmtTypePinRenderers: Record<MgmtTypeClassification, SimpleRenderer> = {
  DirectOp: new SimpleRenderer({ symbol: makeMgmtTypeSymbol(getDirectOperatedPin()) }),
  Lease: new SimpleRenderer({ symbol: makeMgmtTypeSymbol(getLeasePin()) }),
  Contract: new SimpleRenderer({ symbol: makeMgmtTypeSymbol(getThirdPartyPin()) }),
  Unknown: new SimpleRenderer({ symbol: makeMgmtTypeSymbol(getUnknownMgmtTypePin()) })
};

/**
 * Server-side WHERE predicate fragment (no leading `AND`) that isolates the
 * explicit gray Unknown state for layer-14 `MgmtType`: null, blank, or any
 * value outside the three confirmed GIS codes.
 */
const mgmtTypeUnknownWhere =
  `(MgmtType IS NULL OR MgmtType = '' OR MgmtType NOT IN ('DirectOp','Lease','Contract'))`;

/**
 * Boundary pin-layer definitions keyed on the three confirmed layer-14
 * `MgmtType` GIS codes plus an explicit gray Unknown layer — the direct
 * `MgmtType` replacement for the old four `ManagedBy` operation layers.
 * Each entry preserves the existing `STATUS='Active'` server-side predicate
 * (DataScope/status filtering is applied on top of these by the consumer,
 * exactly as it was for the `ManagedBy` layers).
 */
export const mgmtTypeOperationLayers: Array<{
  id: string;
  title: string;
  definitionExpression: string;
  value: MgmtTypeClassification;
  renderer: SimpleRenderer;
}> = [
  {
    id: 'mgmt-type-direct-op',
    title: mgmtTypeVisuals.DirectOp.label,
    definitionExpression: `MgmtType='DirectOp' and STATUS='Active'`,
    value: 'DirectOp',
    renderer: mgmtTypePinRenderers.DirectOp
  },
  {
    id: 'mgmt-type-lease',
    title: mgmtTypeVisuals.Lease.label,
    definitionExpression: `MgmtType='Lease' and STATUS='Active'`,
    value: 'Lease',
    renderer: mgmtTypePinRenderers.Lease
  },
  {
    id: 'mgmt-type-contract',
    title: mgmtTypeVisuals.Contract.label,
    definitionExpression: `MgmtType='Contract' and STATUS='Active'`,
    value: 'Contract',
    renderer: mgmtTypePinRenderers.Contract
  },
  {
    id: 'mgmt-type-unknown',
    title: mgmtTypeVisuals.Unknown.label,
    // Same STATUS scope constraint as the valid categories — an explicit
    // Unknown layer must not become an unrestricted/unscoped bypass.
    definitionExpression: `${mgmtTypeUnknownWhere} and STATUS='Active'`,
    value: 'Unknown',
    renderer: mgmtTypePinRenderers.Unknown
  }
];

/** `outFields` shared by all `mgmtTypeOperationLayers` pin layers. */
export const mgmtTypePinLayerOutFields = ['OBJECTID', 'MgmtType', 'BusinessUnitNumber', 'FARMNUM', 'Division', 'PropID'];

/**
 * Cluster pin-layer configuration for one `MgmtType` pin layer — mirrors
 * `clusterPinLayerConfig` from `./utils.ts` but scoped to the `MgmtType`
 * field so cluster pin layers request the field they classify on instead of
 * `ManagedBy`.
 *
 * @param name - The `MgmtTypeClassification` this layer represents.
 * @returns Cluster pin layer configuration with minScale, maxScale, id, layerName, and outFields.
 *
 * @example
 * mgmtTypeClusterPinLayerConfig('DirectOp').id // 'pin-cluster: DirectOp'
 */
export const mgmtTypeClusterPinLayerConfig = (name: MgmtTypeClassification) => ({
  minScale: 0,
  maxScale: 1000000,
  id: `pin-cluster: ${name}`,
  layerName: `pin-cluster: ${name}`,
  outFields: mgmtTypePinLayerOutFields
});

/**
 * Builds the cluster (aggregate) marker symbol for one `MgmtType` category,
 * reusing the same pin/color as the category's individual-feature symbol so
 * clustered and un-clustered pins never disagree.
 *
 * @param value - The `MgmtTypeClassification` to build a cluster symbol for.
 * @returns A picture-marker symbol config for use as `featureReduction.symbol`.
 *
 * @example
 * createMgmtTypeClusterSymbol('Lease').url // resolves to the lease pin asset
 */
export const createMgmtTypeClusterSymbol = (value: MgmtTypeClassification) => {
  const visual = mgmtTypeVisuals[value];
  return {
    ...pinSymbolDefaultConfig,
    width: `${32 * 20}px`,
    height: `${38 * 20}px`,
    url: visual.getPin()
  };
};

/**
 * Builds an independent `FeatureReductionCluster` configuration for one
 * `MgmtType` pin layer. Returns a fresh object (spread from the shared
 * `defaultFeatureReductionV2` base) on every call rather than a shared
 * mutable singleton — assigning `.symbol` on a shared object reference
 * across multiple layers in a loop would let the last-assigned symbol win
 * for every layer's clusters.
 *
 * @param value - The `MgmtTypeClassification` this feature-reduction config's cluster symbol represents.
 * @returns A new `FeatureReductionCluster` object with a category-specific cluster symbol.
 *
 * @example
 * createMgmtTypeFeatureReduction('Contract').symbol // the Contractor Managed cluster symbol
 */
export const createMgmtTypeFeatureReduction = (value: MgmtTypeClassification): FeatureReductionCluster => ({
  ...defaultFeatureReductionV2,
  symbol: createMgmtTypeClusterSymbol(value) as any
});
